import React, { createContext, useContext, useEffect, useState } from 'react';

const AccessibilityContext = createContext();

export const useAccessibility = () => {
  const context = useContext(AccessibilityContext);
  if (!context) {
    throw new Error('useAccessibility must be used within an AccessibilityProvider');
  }
  return context;
};

export const AccessibilityProvider = ({ children }) => {
  const [reducedMotion, setReducedMotion] = useState(false);
  const [highContrast, setHighContrast] = useState(false);
  const [fontSize, setFontSize] = useState('normal');
  const [announcements, setAnnouncements] = useState([]);

  useEffect(() => {
    // Check for reduced motion preference
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReducedMotion(mediaQuery.matches);

    const handleChange = (e) => setReducedMotion(e.matches);
    mediaQuery.addEventListener('change', handleChange);

    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  useEffect(() => {
    // Apply reduced motion styles
    if (reducedMotion) {
      document.documentElement.style.setProperty('--motion-duration', '0s');
      document.documentElement.style.setProperty('--motion-delay', '0s');
    } else {
      document.documentElement.style.removeProperty('--motion-duration');
      document.documentElement.style.removeProperty('--motion-delay');
    }
  }, [reducedMotion]);

  useEffect(() => {
    // Apply font size changes
    const sizeMap = {
      small: '0.875rem',
      normal: '1rem',
      large: '1.125rem',
      xlarge: '1.25rem'
    };
    
    document.documentElement.style.fontSize = sizeMap[fontSize];
  }, [fontSize]);

  useEffect(() => {
    // Apply high contrast styles
    if (highContrast) {
      document.documentElement.classList.add('high-contrast');
    } else {
      document.documentElement.classList.remove('high-contrast');
    }
  }, [highContrast]);

  const announce = (message, priority = 'polite') => {
    const id = Date.now().toString();
    setAnnouncements(prev => [...prev, { id, message, priority }]);
    
    // Remove announcement after 5 seconds
    setTimeout(() => {
      setAnnouncements(prev => prev.filter(ann => ann.id !== id));
    }, 5000);
  };

  const value = {
    reducedMotion,
    highContrast,
    setHighContrast,
    fontSize,
    setFontSize,
    announce,
    announcements
  };

  return (
    <AccessibilityContext.Provider value={value}>
      {children}
      {/* Live region for announcements */}
      <div
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
        role="status"
      >
        {announcements.map(ann => (
          <div key={ann.id} aria-live={ann.priority}>
            {ann.message}
          </div>
        ))}
      </div>
    </AccessibilityContext.Provider>
  );
};
